<script>
    import debounce from 'lodash.debounce';
    import throttle from 'lodash.throttle';

    export default {
        name: 'Branching',
        data () {
            return {
                connections: {},
                lines: [],
                calcPositions: {},
                positions: {},
                viewBox: '0 0 100 100',
                showCoordinates: false,
                showDevPanel: false,
                coordinateX: 0,
                coordinateY: 0,
                mouseX: 0,
                mouseY: 0,
                viewportCoordinateX: 0,
                viewportCoordinateY: 0,
                itemSize: 1,
                currentFactor: 100,
                zoomMin: 2,
                zoomMax: 50,
                zoomStep: 8,
                zooming: false,
                currentFactor: 100,
                factorRangeSlider: 100,
                variablesInitialized: false,
            }
        },
        props: {
            branchingData: {
                type: Object,
                required: true
            }
        },
        beforeMount() {
            this.resizeHandlerDebounced = debounce(this.resizeHandler, 250);
            for (let [key, value] of Object.entries(this.branchingData.connections)) {
                this.connections[key.replace('node','')] = value.map(n => n);
            }
            for (let [key, value] of Object.entries(this.branchingData.positions)) {
                this.positions[key.replace('node','')] = value;
            }
            this.hasLocalStorage = (function lsTest(){
                var test = 'test';
                try {
                    localStorage.setItem(test, test);
                    localStorage.removeItem(test);
                    return true;
                } catch(e) {
                    return false;
                }
            }());

            if (this.hasLocalStorage && localStorage.currentFactor && typeof parseInt(localStorage.currentFactor, 10) === 'number') {
                this.currentFactor = parseInt(localStorage.currentFactor, 10);
            } else {
                this.currentFactor = parseInt(this.branchingData.factor, 10);
            }
            this.factorRangeSlider = this.currentFactor;
            if (this.branchingData.itemSize) this.itemSize = parseFloat(this.branchingData.itemSize);
            if (this.branchingData.zoomMin) this.zoomMin = parseInt(this.branchingData.zoomMin, 10);
            if (this.branchingData.zoomMax) this.zoomMax = parseInt(this.branchingData.zoomMax, 10);

        },
        mounted() {
            this.init();
        },
        beforeDestroy () {
            window.removeEventListener('resize', this.resizeHandler, false);
            window.removeEventListener('mousemove', this.mouseHandler, false);
            window.removeEventListener('keyup', this.keyHandler, false);
            this.$el.removeEventListener('scroll', this.scrollHandler, false);
        },
        computed: {
            coordinateXNormalized () {
                return Math.round(this.coordinateX / this.currentFactor + this.branchingData.min_x);
            },
            coordinateYNormalized () {
                return Math.round(this.coordinateY / this.currentFactor + this.branchingData.min_y);
            },
            viewBoxWidth () {
                const viewBoxWidth = ((Math.abs(this.branchingData.min_x) + this.branchingData.max_x) * this.currentFactor);
                const branchingWidth = this.variablesInitialized && this.$refs.branching ? this.$refs.branching.getBoundingClientRect().width : 0;
                return viewBoxWidth > branchingWidth ? viewBoxWidth : branchingWidth;
            },
            viewBoxHeight() {
                const viewBoxHeight = ((Math.abs(this.branchingData.min_y) + this.branchingData.max_y) * this.currentFactor);
                const branchingHeight = this.variablesInitialized && this.$refs.branching ? this.$refs.branching.getBoundingClientRect().height : 0;
                return viewBoxHeight > branchingHeight ? viewBoxHeight : branchingHeight;
            },
            // centeringOffsetX() {
            //     const viewBoxWidth = ((Math.abs(this.branchingData.min_x) + this.branchingData.max_x) * this.currentFactor);
            // },
            // centeringOffsetY() {
            //     const viewBoxHeight = ((Math.abs(this.branchingData.min_y) + this.branchingData.max_y) * this.currentFactor);
            // },
        },
        watch: {
            currentFactor(newVal) {
                if (this.variablesInitialized === true) {
                    this.lines = [];
                    this.$nextTick(() => {
                        this.resizeHandlerDebounced();
                    });
                    this.moveViewportToCoordinate(this.viewportCoordinateX, this.viewportCoordinateY);
                    this.factorRangeSlider = newVal;
                    if (this.hasLocalStorage) {
                        localStorage.currentFactor = newVal;
                    }
                }
            },
            factorRangeSlider(newVal) {
                this.factorRangeSliderWatcher(newVal);
            },
            viewportCoordinateX(newVal) {
                if (this.hasLocalStorage) {
                    localStorage.viewportCoordinateX = newVal;
                }
            },
            viewportCoordinateY(newVal) {
                if (this.hasLocalStorage) {
                    localStorage.viewportCoordinateY = newVal;
                }
            }
        },
        methods: {
            init() {
                this.resizeHandler();
                window.addEventListener('resize', this.resizeHandler);
                window.addEventListener('mousemove', this.mouseHandler);
                window.addEventListener('keyup', this.keyHandler);
                this.$refs.branching.addEventListener('scroll', this.scrollHandler);

                if (this.hasLocalStorage && localStorage.viewportCoordinateX && localStorage.viewportCoordinateY) {
                    const restoreX = parseFloat(localStorage.viewportCoordinateX);
                    const restoreY = parseFloat(localStorage.viewportCoordinateY);

                    if (typeof restoreX === 'number' && typeof restoreY === 'number') {
                        this.moveViewportToCoordinate(restoreX, restoreY);
                    } else {
                        this.moveViewportToCoordinate(0, 0);
                    }
                } else {
                    this.moveViewportToCoordinate(0, 0);
                }
                this.$nextTick(() => { this.variablesInitialized = true; }); // needs to be set after render
            },
            coordinateToViewportPositionX(x) {
                return (x - this.branchingData.min_x) * this.currentFactor;
            },
            coordinateToViewportPositionY(y) {
                return (y - this.branchingData.min_y) * this.currentFactor;
            },
            coordinateFromViewportPositionX(x) {
                return x / this.currentFactor + this.branchingData.min_x;
            },
            coordinateFromViewportPositionY(y) {
                return y / this.currentFactor + this.branchingData.min_y;
            },
            updateViewportCoordinate() {
                this.viewportCoordinateX = (this.$refs.branching.scrollLeft + (this.$refs.branching.getBoundingClientRect().width / 2)) / this.currentFactor + this.branchingData.min_x;
                this.viewportCoordinateY = (this.$refs.branching.scrollTop + (this.$refs.branching.getBoundingClientRect().height / 2)) / this.currentFactor + this.branchingData.min_y;
            },
            moveViewportToCoordinate(x, y) {
                this.$refs.branching.scrollLeft = (x - this.branchingData.min_x) * this.currentFactor - (this.$refs.branching.getBoundingClientRect().width / 2);
                this.$refs.branching.scrollTop = (y - this.branchingData.min_y) * this.currentFactor - (this.$refs.branching.getBoundingClientRect().height / 2);
            },
            zoom(zoomFactor) {
                if (this.zooming) return; // Fix: the rangeSlider watcher triggers another zoom. This prevents from multiple zoom actions interfering with eachother sicne the span across multiple ticks
                this.zooming = true;
                this.updateViewportCoordinate();
                const tempViewportCoordinateX = this.viewportCoordinateX;
                const tempViewportCoordinateY = this.viewportCoordinateY;
                this.currentFactor = zoomFactor;
                this.$nextTick(() => {
                    this.moveViewportToCoordinate(tempViewportCoordinateX, tempViewportCoordinateY);
                    this.updateViewportCoordinate();
                    this.zooming = false;
                });
            },
            zoomIn() {
                if (this.currentFactor >= this.zoomMax) {
                    this.zoom(this.zoomMax);
                } else {
                    const current = (this.currentFactor <= this.zoomMin) ? this.zoomMin : this.currentFactor;
                    const target = this.currentFactor + this.zoomStep;
                    const targetRounded = target - target % this.zoomStep;
                    const targetMinMax = Math.min(targetRounded, this.zoomMax);
                    this.zoom(targetMinMax);
                }
            },
            zoomOut() {
                if (this.currentFactor <= this.zoomMin) {
                    this.zoom(this.zoomMin);
                } else {
                    const current = (this.currentFactor >= this.zoomMax) ? this.zoomMax : this.currentFactor;
                    const target = current - this.zoomStep;
                    const targetRounded = target - target % this.zoomStep;
                    const targetMinMax = Math.max(targetRounded, this.zoomMin);
                    this.zoom(targetMinMax);
                }
            },
            resizeHandler() {
                this.lines = this.getLines();
                this.viewBox = this.getViewBox();
                this.updateViewportCoordinate();
            },
            factorRangeSliderWatcher: throttle(function(newVal) {
                if (this.variablesInitialized === true) {
                    this.zoom(newVal);
                    this.currentFactor = newVal;
                }
            }, 100),
            scrollHandler(e) {
                this.updateMouseCoordinates(this.mouseX, this.mouseY);
                this.updateViewportCoordinate();
            },
            mouseHandler(e) {
                this.mouseX = e.pageX;
                this.mouseY = e.pageY;
                this.updateMouseCoordinates(this.mouseX, this.mouseY);
            },
            keyHandler(e) {
                if (e && e.keyCode && e.keyCode == 67) {
                    this.showCoordinates = !this.showCoordinates;
                } else if (e && e.keyCode && e.keyCode == 90) {
                    this.showDevPanel = !this.showDevPanel;
                }
            },
            updateMouseCoordinates(pageX, pageY) {
                const x = pageX + this.$refs.branching.scrollLeft - this.$refs.branching.offsetLeft;
                const y = pageY + this.$refs.branching.scrollTop - this.$refs.branching.offsetTop;
                this.coordinateX = x;
                this.coordinateY = y;
            },
            getLines() {
                let lines = [];
                for (let [nodeId, nodeConnections] of Object.entries(this.connections)) {
                    for (let j = 0; j < nodeConnections.length; j++) {
                        const connection = nodeConnections[j];
                        let i_pos = this.positions[nodeId];
                        let j_pos = this.positions[connection];

                        if (i_pos && j_pos) {
                            lines.push({
                                x1: this.coordinateToViewportPositionX(i_pos.x),
                                y1: this.coordinateToViewportPositionY(i_pos.y),
                                x2: this.coordinateToViewportPositionX(j_pos.x),
                                y2: this.coordinateToViewportPositionY(j_pos.y)
                            });
                        }
                    }
                }
                return lines;
            },
            getViewBox() {
                return '0 0 ' + this.viewBoxWidth + ' ' + this.viewBoxHeight;
            }
        }
    };
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/Branching.vue